/**
 * Shared URL utility functions
 * Centralizes URL parsing and navigation logic used across multiple components
 */

/**
 * Parse URL hash parameters and query string parameters into an object
 * Handles both hash-based routing (e.g., #/orders/pbhnew?PO=123) and regular query strings
 *
 * @returns Record of URL parameters as key-value pairs
 *
 * @example
 * // For URL: #/orders/pbhnew?PO=123&sort=CreationDate,desc
 * // Returns: { PO: "123", sort: "CreationDate,desc" }
 */
export function getUrlVars(): Record<string, string> {
  const vars: Record<string, string> = {};
  const hash = window.location.hash;
  const queryString = hash.includes("?")
    ? hash.split("?")[1]
    : window.location.search.slice(1);

  if (queryString) {
    queryString.split("&").forEach((pair) => {
      const [key, value] = pair.split("=");
      if (key && value) {
        vars[decodeURIComponent(key)] = decodeURIComponent(value);
      }
    });
  }

  return vars;
}

/**
 * Update window location hash safely
 *
 * This function updates the browser's location hash (the part after # in the URL)
 * in a safe manner that handles errors gracefully. It's used to synchronize the
 * URL with application state, enabling bookmarking and sharing of filtered views.
 *
 * Why use this instead of directly setting window.location.hash?
 * - Provides error handling for non-browser contexts (e.g., server-side rendering, tests)
 * - Prevents crashes if window.location is not available
 * - Centralizes hash update logic for consistency across the application
 *
 * Usage:
 * - Called after building URLs to update the browser URL
 * - Used in search handlers, filter change handlers, and navigation logic
 * - Ensures the URL reflects the current application state for bookmarking
 *
 * @param hash - The hash string to set (e.g., "#/orders/pbhnew/?sort=CreationDate,desc&inboundStatus=...")
 *               Should include the leading "#" character
 *
 * @example
 * // Update URL after filtering
 * const url = "#/orders/pbhnew/?inboundStatus=Awaiting%20OffCore%20Info";
 * updateLocationHash(url); // Sets window.location.hash to the new URL
 *
 * @example
 * // Update URL after search
 * const url = "#/orders/pbhnew/?CustomerPO=12345";
 * updateLocationHash(url); // URL becomes: #/orders/pbhnew/?CustomerPO=12345
 */
export const updateLocationHash = (hash: string): void => {
  try {
    window.location.hash = hash;
  } catch (_e) {
    // Ignore navigation errors in non-browser contexts (e.g., tests, SSR)
    // This prevents crashes when window.location is not available
  }
};
